import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { generatePath, useMatch, useNavigate } from "react-router-dom";
import {
  CheckboxFilterTree,
  RadioFilterTree,
} from "@jana-technology/platform-ui";

import { LoadingSpinner } from "components/loaders";
import { deriveRadioTreeBreadcrumbs } from "contexts/filters/helpers";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";
import {
  flattenRadioState,
  generateRadioState,
  useRiskManagementFiltersContext,
} from "contexts/filters/risk-management-filters";
import { OutcomeScenariosContext } from "contexts/scenarios";
import {
  RISK_ROUTE_CHARTS_MAJOR,
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
} from "constants/routing";
import { getRiskChartsPath, parseWellIdNameParam } from "helpers/routing";
import type { RiskPathOption } from "helpers/routing";

import {
  RiskAssetFilters,
  SidebarComponentHeading,
  SidebarSpacer,
} from "./components";
import { HierarchyIdToaster } from "./HierarchyIdToaster";

export const RiskAssetSelection = ({
  hasWellSelection,
  collisionBoundary,
}: {
  hasWellSelection?: boolean;
  collisionBoundary: HTMLDivElement | null;
}) => {
  const {
    hierarchyIdCheckboxes,
    setAppliedHierarchyIdCheckboxStatesRisk,
    hierarchiesWithWellNamesRadiosUnfiltered,
    assetSelectionQueryResult,
    wellIdNameParam,
  } = useGlobalFiltersContext();
  const {
    selectedHierarchyIdCheckboxStatesRisk,
    setSelectedHierarchyIdCheckboxStatesRisk,
    hierarchiesWithWellNamesRadios,
    radioState,
    setRadioState,
    onRadioChange,
    savedRiskMitigationId,
    applyRiskMitigationSelection,
    selectedWellName,
    selectedRiskAssetFilters,
    setSelectedRiskAssetFilters,
    savedRiskAssetFilters,
    setSavedRiskAssetFilters,
    activeFilterCount,
    assetSelectionFilterQueryResult,
  } = useRiskManagementFiltersContext();

  const outcomeScenariosContext = useContext(OutcomeScenariosContext);
  const applyOutcomeScenarioSelection =
    outcomeScenariosContext?.applyOutcomeScenarioSelection;

  const riskMajorRouteMatch = useMatch({
    path: RISK_ROUTE_CHARTS_MAJOR,
    end: false,
  });

  const navigate = useNavigate();

  // keep overall as default tab if no mitigation is applied, otherwise default to mitigation tab
  let riskPathOption: RiskPathOption = RISK_ROUTE_CHARTS_OVERALL_SEGMENT;
  if (savedRiskMitigationId) {
    riskPathOption = RISK_ROUTE_CHARTS_MITIGATION_SEGMENT;
  } else if (riskMajorRouteMatch) {
    riskPathOption = RISK_ROUTE_CHARTS_MAJOR_SEGMENT;
  }

  const [filtersOpened, setFiltersOpened] = useState(false);

  const filtersLoaded =
    assetSelectionFilterQueryResult.dataState === "complete";

  const filterSettingsButton = filtersLoaded ? (
    <RiskAssetFilters
      collisionBoundary={collisionBoundary}
      open={filtersOpened}
      activeFilterCount={activeFilterCount}
      baseFilters={assetSelectionFilterQueryResult.data?.assetSelectionFilter}
      onOpenChange={setFiltersOpened}
      selectedRiskAssetFilters={selectedRiskAssetFilters}
      savedRiskAssetFilters={savedRiskAssetFilters}
      setSelectedRiskAssetFilters={setSelectedRiskAssetFilters}
      setSavedRiskAssetFilters={setSavedRiskAssetFilters}
    />
  ) : undefined;

  const hierarchyIdsKeys = Object.keys(selectedHierarchyIdCheckboxStatesRisk);
  const submitDisabled = hierarchyIdsKeys.every(
    (key) => selectedHierarchyIdCheckboxStatesRisk[key] === false,
  );

  const { flattenedRadioState, expandedRadios } = useMemo(() => {
    const flattenedRadioState = flattenRadioState(radioState);
    return {
      flattenedRadioState,
      expandedRadios: flattenedRadioState.map((item) => item.value),
    };
  }, [radioState]);

  const handleRadioUpdate = useCallback(
    (hierarchyId: string, wellName: string) => {
      const radioTreeBreadcrumbs = deriveRadioTreeBreadcrumbs(
        hierarchiesWithWellNamesRadiosUnfiltered,
        hierarchyId,
        `${hierarchyId}_${wellName}`,
      );
      const last = radioTreeBreadcrumbs.at(-1);
      if (!last) {
        return;
      }
      setRadioState(generateRadioState(radioTreeBreadcrumbs));
    },
    [hierarchiesWithWellNamesRadiosUnfiltered, setRadioState],
  );

  useEffect(() => {
    if (!wellIdNameParam) {
      return;
    }
    const [hierarchyId, wellName] = parseWellIdNameParam(wellIdNameParam);
    handleRadioUpdate(hierarchyId.toString(), wellName);
  }, [wellIdNameParam, handleRadioUpdate]);

  return (
    <HierarchyIdToaster
      selectedHierarchyIdsLength={hierarchyIdsKeys.length}
      submitDisabled={submitDisabled}
    >
      <SidebarSpacer>
        <SidebarComponentHeading>Asset Selection:</SidebarComponentHeading>
        <div>
          <LoadingSpinner
            loading={assetSelectionQueryResult.loading}
            className="h-[79px] w-[280px]"
          >
            {hasWellSelection ? (
              <RadioFilterTree
                key={String(expandedRadios.length > 0)}
                showSearch
                filterSettingsButton={filterSettingsButton}
                radioState={radioState}
                onRadioChange={onRadioChange}
                buttonText="Apply Risk Filters"
                fullFilterTree={hierarchiesWithWellNamesRadios}
                filters={hierarchiesWithWellNamesRadios}
                submitButtonOnClick={() => {
                  applyRiskMitigationSelection();
                  applyOutcomeScenarioSelection?.();
                  const wellNode = flattenedRadioState.find(
                    ({ value }) => value === selectedWellName.value,
                  );
                  if (wellNode) {
                    const [hierarchyId, wellName] = parseWellIdNameParam(
                      wellNode.value,
                    );
                    navigate(
                      generatePath(
                        getRiskChartsPath(
                          hierarchyId,
                          wellName,
                          riskPathOption,
                        ),
                      ),
                      {
                        replace: true, // TODO: replace should be false once mitigation url is refactored
                      },
                    );
                  }
                }}
                defaultExpandedValues={expandedRadios}
              />
            ) : (
              hierarchyIdsKeys.length > 0 && (
                <CheckboxFilterTree
                  checkedState={selectedHierarchyIdCheckboxStatesRisk}
                  submitDisabled={submitDisabled}
                  submitButtonOnClick={() => {
                    applyRiskMitigationSelection();
                    applyOutcomeScenarioSelection?.();
                    setAppliedHierarchyIdCheckboxStatesRisk(
                      selectedHierarchyIdCheckboxStatesRisk,
                    );
                  }}
                  onCheckedChange={setSelectedHierarchyIdCheckboxStatesRisk}
                  buttonText="Apply Risk Filters"
                  fullFilterTree={hierarchyIdCheckboxes}
                  filters={hierarchyIdCheckboxes}
                  defaultExpandedValues={Object.keys(
                    selectedHierarchyIdCheckboxStatesRisk,
                  )}
                />
              )
            )}
          </LoadingSpinner>
        </div>
      </SidebarSpacer>
    </HierarchyIdToaster>
  );
};

export default RiskAssetSelection;
